import { Component, inject, OnInit } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import {
  CellValueChangedEvent,
  ColDef,
  ColGroupDef,
  GridApi,
  GridOptions,
  ModuleRegistry,
  SideBarDef,
  themeAlpine,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzMessageService } from 'ng-zorro-antd/message';
import { SocialAuthService } from '@abacritt/angularx-social-login';

import { VariablesService } from '../../services/variables/variables.service';
import { environment } from '../../../environments/environment';

ModuleRegistry.registerModules([AllEnterpriseModule]);

export type IYomaColDef = ColDef | ColGroupDef;

@Component({
  selector: 'configuration',
  standalone: true,
  imports: [AgGridAngular, NzButtonModule],
  templateUrl: './configuration.component.html',
  styleUrl: './configuration.component.scss',
})
export class ConfigurationComponent implements OnInit {
  variablesService = inject(VariablesService);
  authService = inject(SocialAuthService);
  message = inject(NzMessageService);
  theme = themeAlpine;
  gridApi!: GridApi;
  rowData?: any[];
  dataChanged = false;
  saving = false;
  gridLoading = true;
  superusers: string[] = JSON.parse(environment.adminToolSuperusers);
  isSuperUser = false;

  public gridOptions: GridOptions = {
    onCellValueChanged: (event: CellValueChangedEvent) => this.cellValueChanged(event),
    headerHeight: 24,
    groupHeaderHeight: 24,
  };

  defaultColDef: ColDef = {
    sortable: true,
    resizable: true,
    // suppressHeaderMenuButton: true,
    // singleClickEdit: true,
    wrapHeaderText: true,
    autoHeaderHeight: true,
    editable: true,
  };

  sideBar: SideBarDef = {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: {
          suppressRowGroups: true,
          suppressValues: true,
          suppressPivotMode: true,
        },
      },
      'filters',
    ],
  };

  colDefs: IYomaColDef[] = [
    {
      field: 'field',
      headerName: 'YOMA variable',
      filter: 'agTextColumnFilter',
      filterParams: { buttons: ['clear'] },
      editable: false,
    },
    {
      field: 'headerName',
      filter: 'agTextColumnFilter',
      filterParams: { buttons: ['clear'] },
      valueSetter: (params: ValueSetterParams) => {
        const newValue = (params.newValue ?? '').toString().trim();

        if (newValue && this.isDuplicateHeaderName(params, newValue)) {
          this.message.error(`Der Header Name "${newValue}" wird bereits verwendet.`);
          return false;
        }

        params.data.headerName = params.newValue;
        return true;
      },
    },
    {
      field: 'cellRenderer',
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: [null, 'editableCellRenderer', 'trafficLightCellRenderer', 'withModal'],
      },
    },
    {
      field: 'cellRendererParams',
      editable: (params) => !!params.data?.cellRenderer,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: [null, 'leasingMatch', 'onSiteVehicleLocation'],
      },
      valueGetter: (params: ValueGetterParams) => {
        return params.data?.cellRendererParams?.component;
      },
      valueSetter: (params: ValueSetterParams) => {
        if (!params.data.cellRendererParams) params.data.cellRendererParams = { component: '' };
        params.data.cellRendererParams.component = params.newValue;
        return true;
      },
    },
    {
      field: 'editable',
      cellEditor: 'agCheckboxCellEditor',
      cellDataType: 'boolean',
      width: 120,
      editable: () => this.isSuperUser,
    },
    {
      field: 'cellEditor',
      editable: (params) => !!params.data?.editable && this.isSuperUser,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: [null, 'stringCellEditor'] },
    },
    {
      field: 'filter',
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: [null, 'agSetColumnFilter', 'agTextColumnFilter', 'agDateColumnFilter'] },
    },
    {
      field: 'filterParams',
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: [null, 'textFilterParams', 'dateFilterParams', 'distinctValuesFilterParams', 'mappedValuesFilterParams'] },
    },
    {
      field: 'groupName',
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: [
          'YOMA Intern',
          'Tracking',
          'Bestellung',
          'Adressdaten',
          'Kunde',
          'Bemerkungen',
          'Fahrzeug',
          'Sonstige',
          'Verbrauch',
          'Datentransfer',
          'Eigene Bemerkungsfelder',
          'Autohaus-Schnittstellendaten',
          'Schnittstellen Felder',
        ],
      },
      filter: 'agSetColumnFilter',
    },
    { field: 'sortIndex' },
    {
      field: 'valueFormatter',
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: [null, 'dateFormater', 'dateAndTimeFormater', 'daysFormater', 'currencyFormater', 'mappedFormater'] },
    },
    {
      field: 'valueGetter',
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: [null, 'dotValueGetter'] },
    },
    {
      headerName: 'MA default',
      groupId: 'ma',
      children: [
        {
          width: 135,
          field: 'ma_tenant_default_attribute',
          headerName: 'Available',
          cellEditor: 'agCheckboxCellEditor',
          cellDataType: 'boolean',
        },
        {
          width: 110,
          field: 'ma_view_default_attributes',
          headerName: 'Active',
          cellEditor: 'agCheckboxCellEditor',
          cellDataType: 'boolean',
        },
      ],
      marryChildren: true
    },
    {
      headerName: 'LC default',
      groupId: 'lc',
      children: [
        {
          width: 135,
          field: 'lc_tenant_default_attribute',
          headerName: 'Available',
          cellEditor: 'agCheckboxCellEditor',
          cellDataType: 'boolean',
        },
        {
          width: 110,
          field: 'lc_view_default_attributes',
          headerName: 'Active',
          cellEditor: 'agCheckboxCellEditor',
          cellDataType: 'boolean',
        },
      ],
      marryChildren: true
    },
    {
      headerName: 'FPM default',
      groupId: 'fpm',
      children: [
        {
          width: 135,
          field: 'fm_tenant_default_attribute',
          headerName: 'Available',
          cellEditor: 'agCheckboxCellEditor',
          cellDataType: 'boolean',
        },
        {
          width: 110,
          field: 'fm_view_default_attributes',
          headerName: 'Active',
          cellEditor: 'agCheckboxCellEditor',
          cellDataType: 'boolean',
        },
      ],
      marryChildren: true
    },
  ];

  ngOnInit(): void {
    this.variablesService.getColumnDefinitions().subscribe((res) => {
      this.rowData = res;
      this.gridLoading = false;
    });
    this.authService.authState.subscribe((user) => {
      const email = user?.email?.toLowerCase() ?? '';
      this.isSuperUser = this.superusers.map(e => e.toLowerCase()).includes(email);
    });
  }

  onGridReady(params: any) {
    this.gridApi = params.api;
  }

  private isDuplicateHeaderName(params: ValueSetterParams, newValue: string): boolean {
    const normalizedValue = newValue.toLowerCase();
    let duplicateFound = false;

    params.api.forEachNode((node) => {
      if (node.data === params.data) return;

      const existingValue = (node.data?.headerName ?? '').toString().trim().toLowerCase();
      if (existingValue && existingValue === normalizedValue) {
        duplicateFound = true;
      }
    });

    return duplicateFound;
  }

  cellValueChanged(event: CellValueChangedEvent) {
    switch (event.colDef.field) {
      case 'editable':
        if (!event.newValue) {
          delete event.data.editable;
          delete event.data.cellEditor;
        }
        break;

      case 'cellRenderer': {
        const rowNode = this.gridApi.getRowNode(event.node.id!);
        if (event.newValue) {
          rowNode?.updateData({ ...event.data, cellRendererParams: { component: '' } });
        } else {
          const { cellRendererParams, ...data } = event.data;
          delete data.cellRenderer;
          rowNode?.updateData(data);
        }
        break;
      }

      default:
        break;
    }

    this.dataChanged = true;
  }

  save() {
    const data: any[] = [];
    this.gridApi.forEachNode((node) => {
      if (node.data?.editable === null) delete node.data.editable;
      data.push(node.data);
    });

    this.saving = true;
    this.variablesService.saveColumnDefinition(data).subscribe({
      next: () => {
        this.saving = false;
        this.dataChanged = false;
      },
      error: () => {
        this.saving = false;
      },
    });
  }
}
